import * as THREE from 'three';
import type { TerrainMeta, TerrainLoadResult } from './types';

const MAGIC            = 0x52524554;  // 'TERR' little-endian
const FLAG_RIM_NORMALS = 0x1;

// ── Strata tuning ────────────────────────────────────────────────────────────
// CPU-side
/** Arc-length units per one noise cycle along the boundary (X density).
 *  Lower → fewer, wider folds. Higher → more, tighter folds. */
const STRATA_X_SCALAR   = 0.3;

// GPU-side (injected as GLSL literals)
/** Number of strata bands at 1× exaggeration.
 *  Primary moiré control: reduce to 8–12 to space layers further apart. */
const STRATA_LAYER_COUNT  = 8;
/** Y noise frequency: how fast folds oscillate vertically.
 *  Lower → wider, more gradual bends. Higher → tighter oscillations. */
const STRATA_NOISE_Y_FREQ = 3.0;
/** Fold amplitude as a fraction of total rim height (0–1).
 *  0 = perfectly flat layers. 0.5 = folds up to ±25 % of rim height. */
const STRATA_FOLD_AMP     = 0.45;
/** Separator minimum width as a fraction of one band height.
 *  The per-layer hash adds up to SEP_WIDTH_VAR on top of this. */
const STRATA_SEP_MIN      = 0.12;
/** Additional separator width variation driven by per-layer hash. */
const STRATA_SEP_VAR      = 0.14;
/** Zone noise Y frequency: how often sandstone↔slate colour zones switch.
 *  Lower → larger colour patches. */
const STRATA_ZONE_Y_FREQ  = 1.4;
/** Zone noise X frequency relative to vRimU (already scaled by X_SCALAR). */
const STRATA_ZONE_X_FREQ  = 0.28;

// Strata shader: 2-D noise over (arc-length, height) creates folded/wavy layers.
// aRimU carries arc-length so noise bends across the whole boundary — not just
// vertically — giving the realistic "folded rock" look.
function makeRimMaterial(yBottom: number, yTopMax: number): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
        roughness: 0.90,
        metalness: 0.0,
        side: THREE.DoubleSide,
    });

    // Shared ref so updateRimExaggeration() can write into the live uniform
    const exagUniform = { value: 1.0 };
    mat.userData['exagUniform'] = exagUniform;

    mat.onBeforeCompile = (shader) => {
        shader.uniforms['uYMin']  = { value: yBottom };
        shader.uniforms['uYMax']  = { value: yTopMax };
        shader.uniforms['uExag']  = exagUniform;

        // ── Vertex ──────────────────────────────────────────────────────────
        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            `#include <common>
            attribute float aRimU;
            varying float vModelY;
            varying float vRimU;`,
        );
        shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            `#include <begin_vertex>
            vModelY = position.y;
            vRimU   = aRimU;`,
        );

        // ── Fragment ─────────────────────────────────────────────────────────
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <common>',
            `#include <common>
            varying float vModelY;
            varying float vRimU;
            uniform float uYMin;
            uniform float uYMax;
            uniform float uExag;

            float _rh2(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
            float _vn2(vec2 p){
                vec2 i=floor(p),f=fract(p),u=f*f*(3.0-2.0*f);
                return mix(mix(_rh2(i),_rh2(i+vec2(1,0)),u.x),
                           mix(_rh2(i+vec2(0,1)),_rh2(i+vec2(1,1)),u.x),u.y);
            }
            float _rh1(float n){ return fract(sin(n)*43758.5453); }`,
        );

        shader.fragmentShader = shader.fragmentShader.replace(
            'vec4 diffuseColor = vec4( diffuse, opacity );',
            `// ── Strata ─────────────────────────────────────────────────────
            // Use world-space Y so layers are fixed in world units.
            // Doubling exaggeration reveals twice as many layers instead of
            // just stretching the same ones.
            float _worldY    = vModelY * uExag;
            float _worldYMin = uYMin   * uExag;
            float _worldYMax = uYMax   * uExag;
            // Normalised 0-1 for noise/zone inputs (shape-preserving)
            float _t = clamp((_worldY - _worldYMin) / max(_worldYMax - _worldYMin, 0.001), 0.0, 1.0);
            // World-space layer thickness = model-space layer / 22 bands at 1× exag
            // → more visible layers at higher exag (recalculates, doesn't stretch)
            float _layerSize = (uYMax - uYMin) / ${STRATA_LAYER_COUNT.toFixed(1)};

            // vRimU already encodes physical arc-length × scalar, ceil'd at the
            // loop close so it is an integer there → value noise tiles seamlessly.
            // No normalisation needed: frequency is baked into the scalar on CPU.
            vec2 _p = vec2(vRimU, _worldY * ${STRATA_NOISE_Y_FREQ.toFixed(1)});
            float _bend = _vn2(_p)             * 0.50
                        + _vn2(_p * 2.2 + 4.3) * 0.30
                        + _vn2(_p * 5.1 + 9.1) * 0.20;
            // Bend displaces in world-Y units, preserving amplitude across exag
            float _worldYn = _worldY + (_bend - 0.5) * (uYMax - uYMin) * ${STRATA_FOLD_AMP.toFixed(2)};
            float _tn = clamp((_worldYn - _worldYMin) / max(_worldYMax - _worldYMin, 0.001), 0.0, 1.0);

            // Layer index directly from world Y — count grows with exaggeration
            float _lt = (_worldYn - _worldYMin) / _layerSize;
            float _li = floor(_lt);
            float _lf = _lt - _li;
            float _lh = _rh1(_li);

            // Slow 2-D zone noise: sandstone (warm) vs slate (cool)
            float _zone = _vn2(vec2(vRimU * ${STRATA_ZONE_X_FREQ.toFixed(2)}, _worldY * ${STRATA_ZONE_Y_FREQ.toFixed(1)}));
            vec3 _sand = mix(vec3(0.28, 0.17, 0.06), vec3(0.55, 0.36, 0.13), _tn);
            vec3 _slat = mix(vec3(0.16, 0.18, 0.20), vec3(0.40, 0.42, 0.44), _tn);
            vec3 _rc   = mix(_sand, _slat, smoothstep(0.38, 0.62, _zone));

            // Per-layer brightness variation
            float _lv = (_lh - 0.5) * 0.22;
            _rc = clamp(_rc + vec3(_lv, _lv*0.65, _lv*0.45), 0.0, 1.0);

            // Soft separators — width = SEP_MIN + hash * SEP_VAR of band height
            float _sf = ${STRATA_SEP_MIN.toFixed(2)} + _lh * ${STRATA_SEP_VAR.toFixed(2)};
            float _sa = clamp(smoothstep(_sf, 0.0, _lf) +
                               smoothstep(1.0-_sf, 1.0, _lf), 0.0, 1.0) * 0.78;

            vec3 _sep = vec3(0.76, 0.68, 0.52);
            vec3 _col = mix(_rc, _sep, _sa);

            // Shadow above each seam for depth/overhang feel
            float _sh = (_lf < 0.14 && _sa < 0.15)
                ? (1.0 - _lf / 0.14) * 0.12 : 0.0;
            vec4 diffuseColor = vec4(max(vec3(0.0), _col - _sh), opacity);`,
        );
    };

    mat.customProgramCacheKey = () => 'rim-strata';
    return mat;
}

// ── Terrain color gradient + water mask ─────────────────────────────────────
// Injects a green→brown→white elevation ramp (replacing the old baked
// colormap.png) plus an optional water-mask blend into the terrain material.
// Both are gated by uniforms so 'color'/'relief' switching (applyShading) is
// a cheap per-frame uniform toggle, not a material/texture swap.
interface GradientUniforms {
    gradientMode: { value: number };
    hasWater: { value: number };
    waterMap: { value: THREE.Texture | null };
}

function attachGradientShader(mat: THREE.MeshStandardMaterial, yMin: number, yMax: number): GradientUniforms {
    const gradientMode = { value: 0 };
    const hasWater     = { value: 0 };
    const waterMap: { value: THREE.Texture | null } = { value: null };

    mat.onBeforeCompile = (shader) => {
        shader.uniforms['uYMin']        = { value: yMin };
        shader.uniforms['uYMax']        = { value: yMax };
        shader.uniforms['uGradientMode'] = gradientMode;
        shader.uniforms['uHasWater']    = hasWater;
        shader.uniforms['uWaterMap']    = waterMap;

        // ── Vertex ──────────────────────────────────────────────────────────
        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            `#include <common>
            uniform float uYMin;
            uniform float uYMax;
            varying float vElevT;
            varying vec2 vWaterUv;`,
        );
        shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            `#include <begin_vertex>
            vElevT = clamp((position.y - uYMin) / max(uYMax - uYMin, 0.001), 0.0, 1.0);`,
        );
        shader.vertexShader = shader.vertexShader.replace(
            '#include <uv_vertex>',
            `#include <uv_vertex>
            vWaterUv = uv;`,
        );

        // ── Fragment ─────────────────────────────────────────────────────────
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <common>',
            `#include <common>
            varying float vElevT;
            varying vec2 vWaterUv;
            uniform float uGradientMode;
            uniform float uHasWater;
            uniform sampler2D uWaterMap;

            // Blue (lowest ground) → green → yellow-green → tan → brown →
            // dark brown (peak — no white). The blue-to-green blend is
            // deliberately short (0-0.10) so it reads as a slight hint at
            // the very lowest elevations, not a dominant band.
            vec3 _elevRamp(float t) {
                vec3 cLow = vec3(0.353, 0.490, 0.560);
                vec3 c0   = vec3(0.353, 0.608, 0.314);
                vec3 c1   = vec3(0.569, 0.725, 0.333);
                vec3 c2   = vec3(0.725, 0.647, 0.373);
                vec3 c3   = vec3(0.569, 0.392, 0.227);
                vec3 c4   = vec3(0.439, 0.282, 0.165);
                vec3 col = cLow;
                col = mix(col, c0, smoothstep(0.00, 0.10, t));
                col = mix(col, c1, smoothstep(0.10, 0.32, t));
                col = mix(col, c2, smoothstep(0.32, 0.50, t));
                col = mix(col, c3, smoothstep(0.50, 0.67, t));
                col = mix(col, c4, smoothstep(0.67, 0.85, t));
                return col;
            }`,
        );
        shader.fragmentShader = shader.fragmentShader.replace(
            'vec4 diffuseColor = vec4( diffuse, opacity );',
            `vec4 diffuseColor = vec4( diffuse, opacity );
            if (uGradientMode > 0.5) {
                vec3 _ramp   = _elevRamp(vElevT);
                float _wmRaw = texture2D(uWaterMap, vWaterUv).r;
                // Mipmapping fades thin rivers toward 0 once they're blended
                // with surrounding dry pixels at distance — boost any
                // meaningful coverage back to fully solid so rivers read as
                // continuous lines instead of fading into dots.
                float _wm    = smoothstep(0.04, 0.35, _wmRaw) * uHasWater;
                vec3 _water  = vec3(0.243, 0.443, 0.616);
                diffuseColor = vec4(mix(_ramp, _water, _wm), opacity);
            }`,
        );
    };

    mat.customProgramCacheKey = () => 'terrain-gradient';
    return { gradientMode, hasWater, waterMap };
}

export async function loadTerrainData(slug: string): Promise<TerrainLoadResult> {
    const base   = `/data/${slug}`;
    const loader = new THREE.TextureLoader();

    const meta = await fetch(`${base}/meta.json`, { cache: 'no-cache' }).then(r => r.json()) as TerrainMeta;
    const v = (version?: string) => version ? `?v=${version}` : '';

    const [bin, maskTex, normalTex, waterTex] = await Promise.all([
        fetch(`${base}/terrain.bin${v(meta.terrainVersion)}`).then(r => {
            if (!r.ok) throw new Error(`terrain.bin not found for "${slug}" — run preprocess.sh`);
            return r.arrayBuffer();
        }),
        loader.loadAsync(`${base}/mask.png${v(meta.maskVersion)}`),
        loader.loadAsync(`${base}/normalmap.png${v(meta.normalmapVersion)}`),
        meta.waterVersion
            ? loader.loadAsync(`${base}/water_mask.png${v(meta.waterVersion)}`)
            : Promise.resolve(null),
    ]);

    // ── Parse header (version 2: 10 × uint32 = 40 bytes) ────────────────────
    const hdr = new Uint32Array(bin, 0, 10);
    if (hdr[0] !== MAGIC) throw new Error('terrain.bin: bad magic — rebuild with generate_terrain_mesh.py');
    const version      = hdr[1];
    const segX         = hdr[2];
    const segZ         = hdr[3];
    const vCount       = hdr[4];
    const iCount       = hdr[5];
    const rvCount      = hdr[6];
    const riCount      = hdr[7];
    const flags        = version >= 2 ? hdr[8] : 0;
    const hasRimNormals = !!(flags & FLAG_RIM_NORMALS);

    let off = 40;  // 10 × 4 bytes

    const positions = new Float32Array(bin, off, vCount * 3);  off += vCount * 3 * 4;
    const uvs       = new Float32Array(bin, off, vCount * 2);  off += vCount * 2 * 4;
    const indices   = new Uint32Array (bin, off, iCount);      off += iCount * 4;

    // ── Terrain mesh ─────────────────────────────────────────────────────────
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('uv',       new THREE.BufferAttribute(uvs,       2));
    geo.setIndex(new THREE.BufferAttribute(indices, 1));
    geo.computeVertexNormals();
    geo.computeBoundingBox();

    const texList: THREE.Texture[] = [maskTex, normalTex];
    if (waterTex) texList.push(waterTex);
    for (const tex of texList) {
        tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.generateMipmaps = true;
    }

    const ns  = meta.world.normalScale ?? 1.0;
    const mat = new THREE.MeshStandardMaterial({
        normalMap:   normalTex,
        normalScale: new THREE.Vector2(ns, ns),
        alphaMap:    maskTex,
        transparent: true,
        alphaTest:   0.5,
        roughness:   0.85,
        metalness:   0.0,
        side: THREE.DoubleSide,
    });

    const bbox = geo.boundingBox!;
    const gradUniforms = attachGradientShader(mat, bbox.min.y, bbox.max.y);
    gradUniforms.waterMap.value = waterTex ?? maskTex;  // dummy sampler target when no water data
    if (waterTex) gradUniforms.hasWater.value = 1;
    mat.userData['gradientUniforms'] = gradUniforms;

    const mesh = new THREE.Mesh(geo, mat);
    mesh.receiveShadow = true;
    mesh.name = 'terrain';
    mesh.userData['baseScale']   = meta.world.elevationScale;
    mesh.userData['normalScale'] = ns;
    mesh.userData['normalTex']   = normalTex;
    mesh.userData['waterTex']    = waterTex;
    mesh.userData['meta']        = meta;
    mesh.userData['slug']        = slug;

    // ── Rim mesh ──────────────────────────────────────────────────────────────
    let rim: THREE.Mesh | null = null;
    if (rvCount > 0 && riCount > 0) {
        const rimPos = new Float32Array(bin, off, rvCount * 3);  off += rvCount * 3 * 4;

        const rimDepth = meta.world.elevationScale * 0.4;
        const yBottom  = -rimDepth;

        // Vertices are interleaved: top₀, bot₀, top₁, bot₁ …
        // UV.X = previousUVX + length(segment) * scalar
        // Last vertex: ceil(UVX) so it lands on a whole number → seamless wrap.
        let yTopMax = yBottom;
        let uvX     = 0;
        let prevX   = rimPos[0], prevZ = rimPos[2];
        const rimU  = new Float32Array(rvCount);
        const lastI = rvCount - 2;   // index of last top vertex

        for (let i = 0; i < rvCount; i += 2) {
            if (rimPos[i * 3 + 1] > yTopMax) yTopMax = rimPos[i * 3 + 1];
            if (i > 0) {
                const dx = rimPos[i * 3] - prevX, dz = rimPos[i * 3 + 2] - prevZ;
                uvX += Math.sqrt(dx * dx + dz * dz) * STRATA_X_SCALAR;
            }
            prevX = rimPos[i * 3]; prevZ = rimPos[i * 3 + 2];
            const u = (i === lastI) ? Math.ceil(uvX) : uvX;
            rimU[i]     = u;   // top vertex
            rimU[i + 1] = u;   // bottom vertex (same XZ position)
        }
        const totalUV = rimU[lastI];   // always an integer after ceil

        const rimGeo = new THREE.BufferGeometry();
        rimGeo.setAttribute('position', new THREE.BufferAttribute(rimPos, 3));
        rimGeo.setAttribute('aRimU',    new THREE.BufferAttribute(rimU,   1));

        if (hasRimNormals) {
            const rimNrm = new Float32Array(bin, off, rvCount * 3);  off += rvCount * 3 * 4;
            rimGeo.setAttribute('normal', new THREE.BufferAttribute(rimNrm, 3));
        }

        const rimIdx = new Uint32Array(bin, off, riCount);
        rimGeo.setIndex(new THREE.BufferAttribute(rimIdx, 1));

        if (!hasRimNormals) rimGeo.computeVertexNormals();

        rim = new THREE.Mesh(rimGeo, makeRimMaterial(yBottom, yTopMax));
        rim.name = 'terrain-rim';
    }

    // ── Y values for POI height sampling ─────────────────────────────────────
    const terrainY = new Float32Array(vCount);
    for (let i = 0; i < vCount; i++) terrainY[i] = positions[i * 3 + 1];

    return { meta, mesh, rim, terrainY, segX, segZ, rawPositions: positions, rawUVs: uvs };
}

export const TILE_EXT: 'webp' | 'jpg' =
    (window as unknown as { __TILE_FORMAT__?: string }).__TILE_FORMAT__ === 'webp' ? 'webp' : 'jpg';

// Apply a shading mode to a terrain mesh ('color' or 'relief' — 'satellite'
// is handled separately by main.ts via the tiled LOD system in tiles.ts).
// Cheap uniform toggle (see attachGradientShader) — no material/texture swap.
export async function applyShading(mesh: THREE.Mesh, mode: string): Promise<void> {
    const mat = mesh.material as THREE.MeshStandardMaterial;
    const gradUniforms = mat.userData['gradientUniforms'] as GradientUniforms | undefined;

    if (mode === 'color') {
        mat.color.set(0xffffff);
        if (gradUniforms) gradUniforms.gradientMode.value = 1;
    } else {
        mat.color.set(0xcfc8ba);
        if (gradUniforms) gradUniforms.gradientMode.value = 0;
    }
    mat.wireframe  = false;
    mat.needsUpdate = true;
}

export function disposeTerrain(mesh: THREE.Mesh): void {
    if (!mesh) return;
    mesh.geometry.dispose();
    const mat = mesh.material as THREE.MeshStandardMaterial;
    _disposeMaterial(mat);
    const waterTex = mesh.userData['waterTex'] as THREE.Texture | null | undefined;
    if (waterTex) waterTex.dispose();
}

export function updateRimExaggeration(rim: THREE.Mesh | null, exaggeration: number): void {
    if (!rim) return;
    const u = (rim.material as THREE.MeshStandardMaterial).userData['exagUniform'] as { value: number } | undefined;
    if (u) u.value = exaggeration;
}

export function disposeRim(rim: THREE.Mesh): void {
    if (!rim) return;
    rim.geometry.dispose();
    (rim.material as THREE.Material).dispose();
}

function _disposeMaterial(mat: THREE.MeshStandardMaterial): void {
    mat.map?.dispose();
    mat.alphaMap?.dispose();
    mat.normalMap?.dispose();
    mat.dispose();
}
